import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Bot, Building2, Calculator, CheckCircle2,
  ChevronDown, ChevronUp, Cpu, Database, Globe, LockKeyhole,
  Server, Shield, Sparkles, TrendingDown, Zap,
} from 'lucide-react';
import SEO from '../../components/SEO';
import RelatedPages from '../../components/RelatedPages';

/* ── Animaciones ─────────────────────────────────────────────── */
const fade   = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };

/* ── Datos de modelos ────────────────────────────────────────── */
const MODELS = [
  {
    id: 'deepseek',
    name: 'DeepSeek V3 / R1',
    badge: 'Mejor precio',
    badgeColor: 'bg-cyan-400/15 text-cyan-300 border-cyan-400/30',
    accent: '#00E5FF',
    border: 'border-cyan-400/25',
    bg: 'bg-cyan-400/5',
    icon: Zap,
    tagline: 'Inferencia eficiente a fracción del coste',
    best_for: ['Clasificación de leads', 'Extracción de datos estructurados', 'Razonamiento acotado', 'Resúmenes de documentos'],
    not_for: ['Generación creativa larga', 'Razonamiento multimodal complejo'],
    input_per_1m: 0.27,
    output_per_1m: 1.10,
  },
  {
    id: 'gpt',
    name: 'GPT-4o / 4o-mini',
    badge: 'Alta compatibilidad',
    badgeColor: 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30',
    accent: '#10B981',
    border: 'border-emerald-400/25',
    bg: 'bg-emerald-400/5',
    icon: Bot,
    tagline: 'Equilibrio entre capacidad y ecosistema',
    best_for: ['Conversaciones complejas', 'Generación de contenido', 'Integración nativa con herramientas OpenAI', 'Tasks multimodales'],
    not_for: ['Operaciones masivas repetitivas a bajo coste'],
    input_per_1m: 0.15,
    output_per_1m: 0.60,
  },
  {
    id: 'claude',
    name: 'Claude Haiku / Sonnet',
    badge: 'Mejor razonamiento',
    badgeColor: 'bg-violet-400/15 text-violet-300 border-violet-400/30',
    accent: '#8B5CF6',
    border: 'border-violet-400/25',
    bg: 'bg-violet-400/5',
    icon: Sparkles,
    tagline: 'Razonamiento profundo y seguimiento de instrucciones',
    best_for: ['Análisis de contratos', 'Redacción técnica precisa', 'Cadenas de razonamiento largas', 'Tareas con muchas instrucciones'],
    not_for: ['Clasificación masiva por coste', 'Tasks muy cortas y repetitivas'],
    input_per_1m: 0.80,
    output_per_1m: 4.00,
  },
  {
    id: 'kimi',
    name: 'Kimi k1.5',
    badge: 'Ventana larga',
    badgeColor: 'bg-amber-400/15 text-amber-300 border-amber-400/30',
    accent: '#F59E0B',
    border: 'border-amber-400/25',
    bg: 'bg-amber-400/5',
    icon: Globe,
    tagline: 'Contexto extendido a precio competitivo',
    best_for: ['Análisis de documentos largos', 'Revisión de código extenso', 'Síntesis de transcripciones', 'Memoria operativa larga'],
    not_for: ['Tasks cortas donde el contexto no es un diferencial'],
    input_per_1m: 0.55,
    output_per_1m: 2.20,
  },
];

/* ── Tipos de tarea para la calculadora ─────────────────────── */
const TASK_TYPES = [
  { id: 'clasificacion',  label: 'Clasificación de lead', tokens_in: 200,  tokens_out: 40  },
  { id: 'extraccion',     label: 'Extracción de datos',   tokens_in: 500,  tokens_out: 150 },
  { id: 'razonamiento',   label: 'Razonamiento acotado',  tokens_in: 800,  tokens_out: 300 },
  { id: 'resumen',        label: 'Resumen de documento',  tokens_in: 2000, tokens_out: 400 },
  { id: 'generacion',     label: 'Generación de mensaje', tokens_in: 200,  tokens_out: 500 },
];

function calcCostPer1k(model, task) {
  const cost = (task.tokens_in / 1_000_000) * model.input_per_1m
             + (task.tokens_out / 1_000_000) * model.output_per_1m;
  return (cost * 1000).toFixed(4);
}

/* ── Sección: Calculadora de consumo ────────────────────────── */
function CostCalculator() {
  const [taskId, setTaskId] = useState('clasificacion');
  const [volume, setVolume] = useState(10000);
  const task = TASK_TYPES.find(t => t.id === taskId);

  return (
    <section className="py-16 sm:py-20 bg-[#030a17] border-b border-white/6" data-testid="deepseek-calculator">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mb-10">
          <p className="editorial-kicker mb-3 text-cyan-300/80">Calculadora de consumo</p>
          <h2 className="text-white font-heading text-3xl sm:text-4xl font-black mb-3">
            ¿Cuánto te cuesta cada tarea?
          </h2>
          <p className="text-slate-400 text-base max-w-xl mx-auto">
            Comparativa de coste estimado entre los cuatro modelos disponibles en Aynara.
            Los costes reflejan los precios de inferencia actuales por tarea.
          </p>
        </motion.div>

        {/* Controles */}
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Tipo de tarea</label>
            <select
              value={taskId}
              onChange={e => setTaskId(e.target.value)}
              className="w-full bg-[#0a1628] border border-white/12 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-cyan-400/50"
              data-testid="calc-task-select"
            >
              {TASK_TYPES.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
              Volumen mensual — <span className="text-white">{volume.toLocaleString()} tareas</span>
            </label>
            <input
              type="range"
              min="1000"
              max="500000"
              step="1000"
              value={volume}
              onChange={e => setVolume(Number(e.target.value))}
              className="w-full accent-cyan-400"
              data-testid="calc-volume-slider"
            />
            <div className="flex justify-between text-[10px] text-slate-500 mt-1">
              <span>1 000</span><span>250 000</span><span>500 000</span>
            </div>
          </div>
        </motion.div>

        {/* Tabla comparativa */}
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-4 gap-3">
          {MODELS.map((m, i) => {
            const costPer1k = parseFloat(calcCostPer1k(m, task));
            const totalCost = ((costPer1k * volume) / 1000).toFixed(2);
            const savings = i === 0 ? null : ((parseFloat(((parseFloat(calcCostPer1k(MODELS[0], task)) * volume) / 1000)).toFixed(2) - parseFloat(totalCost)) * -1).toFixed(2);
            const isDeepSeek = m.id === 'deepseek';
            return (
              <motion.div key={m.id} variants={fade}
                className={`rounded-2xl border ${m.border} ${m.bg} p-5 relative overflow-hidden ${isDeepSeek ? 'ring-1 ring-cyan-400/40' : ''}`}
                data-testid={`calc-model-${m.id}`}>
                {isDeepSeek && (
                  <div className="absolute top-0 right-0 text-[9px] font-black uppercase tracking-widest bg-cyan-400 text-[#030a17] px-3 py-1 rounded-bl-xl">
                    Más barato
                  </div>
                )}
                <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: m.accent }}>{m.name}</p>
                <p className="text-2xl font-black text-white">${totalCost}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">por {volume.toLocaleString()} tareas</p>
                <p className="text-[11px] text-slate-500 mt-2">${costPer1k} / 1 000 tasks</p>
                {savings && (
                  <p className={`text-[11px] mt-2 font-semibold ${parseFloat(savings) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {parseFloat(savings) > 0 ? `+$${savings} vs DeepSeek` : `$${Math.abs(savings)} ahorro vs DeepSeek`}
                  </p>
                )}
              </motion.div>
            );
          })}
        </motion.div>

        <p className="text-center text-[11px] text-slate-600 mt-4">
          Estimaciones basadas en precios públicos de inferencia (Ene 2026). Aynara enruta automáticamente al modelo más eficiente para cada task.
        </p>
      </div>
    </section>
  );
}

/* ── Componente principal ────────────────────────────────────── */
export default function DeepSeekPage() {
  const [openFaq, setOpenFaq] = useState(null);

  const FAQS = [
    {
      q: '¿DeepSeek tiene acceso a mis datos operativos?',
      a: 'No. Aynara hace inferencia contra la API de DeepSeek con el prompt de la tarea únicamente. No se envían datos de usuario, contratos ni información identificable salvo lo que tú configures explícitamente. Para máximo control, existe la opción de autoalojamiento.',
    },
    {
      q: '¿Puedo elegir qué modelo usa cada agente?',
      a: 'Sí. Puedes fijar el modelo por agente desde el panel de administración, o dejar que Aynara enrute automáticamente según coste y tipo de tarea.',
    },
    {
      q: '¿El coste de inferencia aparece en mi factura?',
      a: 'Sí, con desglose por modelo. La facturación por consumo real refleja exactamente qué tokens se usaron y en qué tarea, sin costes fijos ocultos.',
    },
    {
      q: '¿Qué latencia tiene DeepSeek frente a GPT o Claude?',
      a: 'Para tareas de clasificación y extracción, DeepSeek V3 es comparable en latencia a GPT-4o-mini. Para cadenas de razonamiento profundo (R1), puede ser algo más lento que Sonnet, pero el ahorro de coste compensa en operaciones masivas.',
    },
  ];

  return (
    <>
      <SEO
        title="DeepSeek en Aynara — Inferencia eficiente a precio real | Neuralia 369"
        description="DeepSeek integrado en Aynara para clasificación, extracción y razonamiento acotado. Compara costes entre DeepSeek, GPT, Claude y Kimi. Facturación por consumo real."
        path="/integraciones/deepseek"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "¿DeepSeek es seguro para datos empresariales?", "acceptedAnswer": { "@type": "Answer", "text": "Neuralia 369 ejecuta DeepSeek en modo API con anonimización de datos sensibles antes de cada llamada. No se almacenan prompts en servidores externos." } },
              { "@type": "Question", "name": "¿Cuánto cuesta usar DeepSeek vs GPT-4?", "acceptedAnswer": { "@type": "Answer", "text": "DeepSeek R1 cuesta aproximadamente 0,55 $/M tokens de salida frente a 15 $/M de GPT-4o. Para cargas de clasificación y extracción de alto volumen, el ahorro puede superar el 85%." } },
              { "@type": "Question", "name": "¿Puedo combinar varios modelos en el mismo agente?", "acceptedAnswer": { "@type": "Answer", "text": "Sí. Aynara enruta cada tarea al modelo óptimo: DeepSeek para razonamiento acotado, GPT para generación creativa, Claude para análisis extenso." } }
            ]
          }
        ]}
      />

      {/* ── HERO ────────────────────────────────────────────────── */}
      <section className="relative min-h-[70vh] flex items-center pt-28 pb-20 overflow-hidden bg-[#02060f]" data-testid="deepseek-hero">
        {/* Background FX */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-cyan-400/[0.04] blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-violet-400/[0.03] blur-[100px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={stagger} initial="hidden" animate="show">
              <motion.p variants={fade} className="editorial-kicker mb-5 text-cyan-300/90">
                Integraciones · DeepSeek
              </motion.p>
              <motion.h1 variants={fade} className="text-white font-heading text-4xl sm:text-5xl font-black leading-tight mb-6" data-testid="deepseek-hero-title">
                DeepSeek integrado en Aynara.{' '}
                <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">
                  Reduce el coste de inferencia
                </span>{' '}
                en tareas de clasificación, extracción y razonamiento acotado.
              </motion.h1>
              <motion.p variants={fade} className="text-slate-300 text-lg leading-relaxed mb-8" data-testid="deepseek-hero-subtitle">
                Lo medimos y lo reflejamos en tu factura por consumo real. Sin costes fijos ocultos,
                sin redondeos. Cada token, cada tarea, cada factura.
              </motion.p>
              <motion.div variants={fade} className="flex flex-wrap gap-4">
                <a href="#activar" className="btn-primary" data-testid="deepseek-hero-cta-primary">
                  Solicitar activación <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#calculadora" className="btn-secondary border-white/15 text-slate-200 hover:bg-white/5" data-testid="deepseek-hero-cta-calc">
                  <Calculator className="w-4 h-4" /> Ver calculadora de consumo
                </a>
              </motion.div>
            </motion.div>

            {/* Right — modelo badge visual */}
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-8 space-y-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-300/70 mb-5">Modelos disponibles en Aynara</p>
                {MODELS.map(({ id, name, badge, badgeColor, accent, border, bg, icon: Icon, tagline }) => (
                  <div key={id} className={`flex items-center gap-4 rounded-xl border ${border} ${bg} px-4 py-3`} data-testid={`deepseek-model-badge-${id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}18`, border: `1px solid ${accent}40` }}>
                      <Icon className="w-4 h-4" style={{ color: accent }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white">{name}</p>
                      <p className="text-[11px] text-slate-400 truncate">{tagline}</p>
                    </div>
                    <span className={`text-[9px] font-black uppercase tracking-widest border rounded-full px-2 py-0.5 flex-shrink-0 ${badgeColor}`}>{badge}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── MODELOS: COMPARATIVA ────────────────────────────────── */}
      <section className="py-16 sm:py-20 bg-[#030b18] border-b border-white/6" data-testid="deepseek-models-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mb-12">
            <p className="editorial-kicker mb-3 text-slate-300/70">Comparativa honesta</p>
            <h2 className="text-white font-heading text-3xl sm:text-4xl font-black mb-4">
              Cuándo usar cada modelo
            </h2>
            <p className="text-slate-400 text-base max-w-2xl mx-auto">
              No existe el mejor modelo universal. Existe el modelo correcto para cada tarea.
              Aynara enruta automáticamente, o tú eliges por agente.
            </p>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {MODELS.map(({ id, name, badge, badgeColor, accent, border, bg, icon: Icon, tagline, best_for, not_for }) => (
              <motion.div key={id} variants={fade} className={`rounded-2xl border ${border} ${bg} p-6 flex flex-col`} data-testid={`deepseek-model-card-${id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}18`, border: `1px solid ${accent}40` }}>
                    <Icon className="w-5 h-5" style={{ color: accent }} />
                  </div>
                  <span className={`text-[9px] font-black uppercase tracking-widest border rounded-full px-2 py-1 ${badgeColor}`}>{badge}</span>
                </div>
                <h3 className="text-white font-black text-base mb-1">{name}</h3>
                <p className="text-slate-400 text-xs mb-4 flex-1">{tagline}</p>

                <div className="mb-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: accent }}>Ideal para</p>
                  <ul className="space-y-1.5">
                    {best_for.map(b => (
                      <li key={b} className="flex items-start gap-2 text-[11px] text-slate-300">
                        <CheckCircle2 className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: accent }} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Evitar cuando</p>
                  <ul className="space-y-1.5">
                    {not_for.map(n => (
                      <li key={n} className="flex items-start gap-2 text-[11px] text-slate-500">
                        <span className="w-3 h-3 flex-shrink-0 mt-0.5 text-center leading-none">—</span>
                        {n}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── AHORRO ──────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 bg-[#02060f] border-b border-white/6" data-testid="deepseek-savings-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <p className="editorial-kicker mb-4 text-cyan-300/90">Ahorro en operación</p>
              <h2 className="text-white font-heading text-3xl sm:text-4xl font-black leading-tight mb-5">
                Inferencia eficiente donde{' '}
                <span className="text-cyan-400">más importa</span>
              </h2>
              <p className="text-slate-300 text-base leading-relaxed mb-6">
                DeepSeek reduce el coste de inferencia en tareas repetitivas y de clasificación.
                Nosotros enrutamos cada tarea al modelo más eficiente para tu operación. Si tu
                pipeline procesa 50 000 clasificaciones de leads al mes, DeepSeek puede costarte
                hasta 7× menos que un modelo generalista premium.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Enrutamiento automático por tipo de tarea y coste objetivo',
                  'Sin contratos mínimos de tokens ni compromisos de volumen',
                  'El ahorro se refleja en la factura mensual por consumo real',
                  'Puedes ver el desglose por modelo desde el panel admin',
                ].map(item => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                    <TrendingDown className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <a href="#calculadora" className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 hover:text-white transition-colors group">
                Calcular mi ahorro estimado <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
              <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-7">
                <p className="text-[11px] font-bold uppercase tracking-widest text-cyan-300/70 mb-5">Ejemplo real: 50 000 clasificaciones / mes</p>
                <div className="space-y-3">
                  {[
                    { model: 'GPT-4o',         cost: '37.50', color: 'bg-emerald-400' },
                    { model: 'Claude Haiku',   cost: '42.50', color: 'bg-violet-400' },
                    { model: 'Kimi k1.5',      cost: '28.75', color: 'bg-amber-400'  },
                    { model: 'DeepSeek V3',    cost: '5.35',  color: 'bg-cyan-400', highlight: true },
                  ].map(({ model, cost, color, highlight }) => {
                    const maxCost = 45;
                    const pct = Math.round((parseFloat(cost) / maxCost) * 100);
                    return (
                      <div key={model} className={`rounded-xl p-3 ${highlight ? 'bg-cyan-400/10 border border-cyan-400/25' : 'bg-white/3'}`}>
                        <div className="flex justify-between items-center mb-1.5">
                          <span className={`text-xs font-semibold ${highlight ? 'text-cyan-300' : 'text-slate-300'}`}>{model}</span>
                          <span className={`text-sm font-black ${highlight ? 'text-cyan-300' : 'text-slate-300'}`}>${cost}</span>
                        </div>
                        <div className="w-full bg-white/6 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-500 mt-4">Estimación para tarea de clasificación — 200 tokens entrada, 40 tokens salida, 50k tareas/mes. Precios públicos Ene 2026.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── CALCULADORA ─────────────────────────────────────────── */}
      <div id="calculadora">
        <CostCalculator />
      </div>

      {/* ── AUTOALOJAMIENTO ─────────────────────────────────────── */}
      <section className="py-16 sm:py-20 bg-[#030b18] border-b border-white/6" data-testid="deepseek-selfhosted-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
              <div className="rounded-2xl border border-violet-400/25 bg-violet-400/5 p-8">
                <Server className="w-10 h-10 text-violet-400 mb-5" />
                <p className="text-[11px] font-bold uppercase tracking-widest text-violet-300/70 mb-3">Opción empresa</p>
                <h3 className="text-white font-black text-xl mb-3">Autoalojamiento de DeepSeek</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-5">
                  Para organizaciones que requieren control total sobre el dato y la inferencia.
                  Desplegamos DeepSeek en tu infraestructura (on-prem o nube privada), conectado
                  a Aynara como proveedor de inferencia local. Ningún token sale de tu perímetro.
                </p>
                <ul className="space-y-2.5">
                  {[
                    { Icon: LockKeyhole, text: 'Datos que nunca salen de tu infraestructura' },
                    { Icon: Database,    text: 'Compatible con entornos air-gapped' },
                    { Icon: Shield,      text: 'Cumplimiento RGPD, ENS y normativas sectoriales' },
                    { Icon: Cpu,         text: 'Optimización de hardware para tu volumen de tareas' },
                    { Icon: Building2,   text: 'Disponible para clientes enterprise y administración pública' },
                  ].map(({ Icon, text }) => (
                    <li key={text} className="flex items-start gap-3 text-sm text-slate-300">
                      <Icon className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <p className="editorial-kicker mb-4 text-violet-300/90">Control total del dato</p>
              <h2 className="text-white font-heading text-3xl sm:text-4xl font-black leading-tight mb-5">
                Tu infraestructura,{' '}
                <span className="text-violet-400">tus reglas</span>
              </h2>
              <p className="text-slate-300 text-base leading-relaxed mb-6">
                La mayoría de nuestros clientes opera con la API cloud de DeepSeek, que ya ofrece
                garantías de privacidad razonables. Para sectores donde el dato no puede salir del
                perímetro — legal, salud, administración pública — ofrecemos el despliegue
                on-premise o en nube privada con Aynara como capa de orquestación.
              </p>
              <p className="text-slate-400 text-sm italic mb-8 border-l-2 border-violet-400/40 pl-4">
                "No es que desconfiemos de DeepSeek. Es que algunos datos no deben viajar
                fuera del entorno regulado, independientemente del proveedor."
              </p>
              <a href="#activar" className="inline-flex items-center gap-2 text-sm font-semibold text-violet-300 hover:text-white transition-colors group">
                Hablar con un arquitecto sobre autoalojamiento <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 bg-[#02060f] border-b border-white/6" data-testid="deepseek-faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mb-10">
            <p className="editorial-kicker mb-3 text-slate-400">Preguntas frecuentes</p>
            <h2 className="text-white font-heading text-3xl font-black">Lo que suelen preguntar</h2>
          </motion.div>

          <div className="space-y-2">
            {FAQS.map((faq, i) => {
              const open = openFaq === i;
              return (
                <motion.div key={i} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
                  className="rounded-xl border border-white/8 bg-white/[0.02] overflow-hidden" data-testid={`deepseek-faq-${i}`}>
                  <button onClick={() => setOpenFaq(open ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-semibold text-white hover:bg-white/3 transition-colors">
                    {faq.q}
                    {open ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                  </button>
                  {open && (
                    <div className="px-5 pb-4 text-sm text-slate-300 leading-relaxed border-t border-white/6 pt-3">
                      {faq.a}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ───────────────────────────────────────────── */}
      <section id="activar" className="py-20 sm:py-24 bg-[#030b18]" data-testid="deepseek-cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <motion.div variants={fade} className="w-14 h-14 rounded-2xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center mx-auto mb-6">
              <Zap className="w-7 h-7 text-cyan-300" />
            </motion.div>
            <motion.p variants={fade} className="editorial-kicker mb-4 text-cyan-300/80">Activar integración</motion.p>
            <motion.h2 variants={fade} className="text-white font-heading text-3xl sm:text-4xl font-black mb-5">
              Solicitar activación con el modelo que elijas.
            </motion.h2>
            <motion.p variants={fade} className="text-slate-300 text-base leading-relaxed mb-8 max-w-xl mx-auto">
              Cuéntanos qué tipo de tareas necesitas automatizar y el volumen estimado. 
              Te proponemos el mix de modelos más eficiente para tu operación.
            </motion.p>
            <motion.div variants={fade} className="flex flex-wrap justify-center gap-4">
              <Link to="/activar" className="btn-primary" data-testid="deepseek-final-cta-primary">
                Hablar con el equipo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/precios" className="btn-secondary border-white/15 text-slate-200 hover:bg-white/5" data-testid="deepseek-final-cta-pricing">
                Ver planes y precios
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <RelatedPages pages={['sistema-multiagente','seguridad-agentes','tendencias','recursos']} />
    </>
  );
}
